import { Box, Checkbox, FormControlLabel, IconButton } from "@material-ui/core"
import AddIcon from "@material-ui/icons/Add"
import CreateIcon from "@material-ui/icons/Create"
import DeleteIcon from "@material-ui/icons/Delete"
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox"
import Share from "@material-ui/icons/Share"
import VisibilityIcon from "@material-ui/icons/Visibility"
import Visibility from "@material-ui/icons/Visibility"
import { useMemo, useState } from "react"
import type { MouseEvent, ReactElement } from "react"

import type { IConfirmationDialogProps } from ".."
import { CardTemplate, ConfirmationDialog, ContextMenuButton, IconMenuItem } from ".."
import { useJwtToken } from "../../../hooks"
import type { ProjectDto } from "../../../models"
import { LastEditDateCaption, SubTitle1 } from "../../atoms"

import "./project-card.styles.scss"
import { useTranslation } from "react-i18next"
import { CardThumbnail } from "../../atoms/card-thumbnail/card-thumbnail.component"

export interface IProjectCardOwnProps {
  disabled?: boolean
  project: ProjectDto
  selectable?: boolean
  selected?: boolean | undefined
  disableEditing?: boolean | undefined
  showInFolder?: boolean | undefined
  disablePreview?: boolean | undefined
  onProjectClick?: (project: ProjectDto) => void
  onChangeBundle?: (comparison: ProjectDto) => void
  onRemove?: (project: ProjectDto) => void
  onRequestUpdate?: (project: ProjectDto) => void
  onProjectPreview?: (project: ProjectDto) => void
  onShareUrlClick?: (projectId: string) => void
}

export function ProjectCard(props: IProjectCardOwnProps): ReactElement {
  const {
    disabled,
    project,
    selectable,
    selected,
    disableEditing,
    showInFolder,
    disablePreview,
    onProjectClick,
    onChangeBundle,
    onRemove,
    onRequestUpdate,
    onProjectPreview,
    onShareUrlClick
  } = props

  const { isTeacher } = useJwtToken()
  const { t } = useTranslation(["myBosatlas", "general"])

  const [confirmDialogProps, setConfirmDialogProps] = useState<IConfirmationDialogProps>({
    open: false,
    confirmText: t("general:dialog.delete"),
    title: "",
    bodyText: "",
    onConfirm: () => {
      if (onRemove) onRemove(project)
    },
    onCancel: closeConfirmDialog
  })

  const shouldAllowActions: boolean = useMemo<boolean>(() => {
    if (isTeacher) return true
    if (project.sharedClass) return false
    return true
  }, [isTeacher, project])

  function handleURLShareClick(): void {
    if (onShareUrlClick) onShareUrlClick(project.id)
  }

  function handleProjectClick(event?: MouseEvent): void {
    if (event) event.stopPropagation()
    if (onProjectClick) onProjectClick(project)
  }

  function handleProjectPreview(event: MouseEvent): void {
    event.stopPropagation()
    if (onProjectPreview) onProjectPreview(project)
  }

  function handleChangeBundle(): void {
    if (onChangeBundle) onChangeBundle(project)
  }

  function handleRemove(): void {
    const title: string = project.sharedClass
      ? t("myBosatlas:remove.removeSharedProject")
      : t("myBosatlas:remove.removeProject")
    const bodyText: string = project.sharedClass
      ? t("myBosatlas:remove.removeSharedProjectBodyText")
      : t("myBosatlas:remove.removeProjectBodyText")
    setConfirmDialogProps({
      ...confirmDialogProps,
      open: true,
      title,
      bodyText
    })
  }

  function handleRequestUpdate(): void {
    if (onRequestUpdate) onRequestUpdate(project)
  }

  function getChangeBundleTitle(): string {
    return showInFolder ? t("myBosatlas:cards.moveTo") : t("myBosatlas:cards.addFolder")
  }

  function ChangeBundleIcon(): ReactElement {
    return showInFolder ? <MoveToInboxIcon color="primary" /> : <AddIcon color="primary" />
  }

  function getSharedProjectCaptionString(): string | undefined {
    if (!project.sharedClass || !project.lastModified) return undefined
    return isTeacher
      ? t("myBosatlas:cards.lastSharedOnDate", {
          date: project.lastModified?.toString(),
          class: project.sharedClass
        })
      : t("myBosatlas:cards.sharedOnDate", {
          date: project.lastModified?.toString()
        })
  }

  function closeConfirmDialog(): void {
    setConfirmDialogProps({ ...confirmDialogProps, open: false })
  }

  function getRemoveTitle(): string {
    return showInFolder ? t("myBosatlas:cards.deleteFolder") : t("general:dialog.delete")
  }

  function Header(): ReactElement {
    return (
      <Box className="project-card-header">
        <FormControlLabel
          disabled={disabled && !selected}
          control={
            <Checkbox
              color="primary"
              disabled={disabled && !selected}
              checked={selected}
              onClick={handleProjectClick}
            />
          }
          label={t("myBosatlas:cards.add")}
          className="selectable-checkbox"
        />
        {!disablePreview && (
          <IconButton className="preview-button" onClick={handleProjectPreview} size="small">
            <Visibility />
          </IconButton>
        )}
      </Box>
    )
  }

  return (
    <Box>
      <CardTemplate
        header={selectable ? <Header /> : undefined}
        onClick={handleProjectClick}
        image={<CardThumbnail image={project.assetThumbnailUrl} />}
        className="project-card"
        selected={selected}
        big
      >
        <Box className="project-card-content">
          <Box>
            <SubTitle1>{project.title}</SubTitle1>
            <LastEditDateCaption date={project.lastModified} customString={getSharedProjectCaptionString()} />
          </Box>
          {!disableEditing && !selectable && (
            <ContextMenuButton className="project-context" closeOnSelect>
              <IconMenuItem
                icon={<VisibilityIcon color="primary" />}
                label={t("myBosatlas:cards.showMap")}
                onClick={handleProjectClick}
              />

              {shouldAllowActions && [
                <IconMenuItem
                  key="share"
                  icon={<Share color="primary" />}
                  label={t("myBosatlas:cards.shareURLMap")}
                  onClick={handleURLShareClick}
                />,
                <IconMenuItem
                  key="edit-name"
                  icon={<CreateIcon color="primary" />}
                  label={t("myBosatlas:cards.changeName")}
                  onClick={handleRequestUpdate}
                />,
                <IconMenuItem
                  key="change-bundle"
                  icon={<ChangeBundleIcon />}
                  label={getChangeBundleTitle()}
                  onClick={handleChangeBundle}
                />,
                <IconMenuItem
                  key="delete"
                  icon={<DeleteIcon color="primary" />}
                  label={getRemoveTitle()}
                  onClick={handleRemove}
                />
              ]}
            </ContextMenuButton>
          )}
        </Box>
      </CardTemplate>
      <ConfirmationDialog {...confirmDialogProps} />
    </Box>
  )
}
